import { observable, action, reaction, computed } from 'mobx'
import userStore from './userStore'
import routingStore from './routingStore'
import commonStore from './commonStore'
import errorStore from './errorStore'
import api from '../api'

class ProfileStore {

  @observable customer = {
    salutation: '',
    firstname: '',
    lastname: '',
    dateOfBirth: '',
    street: '',
    city: '',
    country: '',
    phoneMobile: '',
  }

  @observable password = ''
  @observable passwordConfirm = ''
  @observable changingPassword = 'unchanged' // unchanged / pending / done
  @observable passwordErrors = []

  @observable email = ''
  @observable changingEmail = 'unchanged' // unchanged / pending / done
  @observable emailErrors = []
  @observable emailChanged = false

  @observable deactivateAccountDone = 'unchanged' // unchanged / pending / done
  @observable accountErrors = []

  //PersonalData
  @observable salutation = ''
  @observable firstname = ''
  @observable lastname = ''
  @observable dateOfBirth = ''
  @observable street = ''
  @observable city = ''
  @observable postcode = ''
  @observable country = ''
  @observable phoneFixed = ''
  @observable phoneMobile = ''
  @observable updatingPersonalData = 'unchanged' // unchanged / pending / done
  @observable personalDataErrors = []

  //Newsletter
  @observable newsletterBrand = false
  @observable newsletterNews = false
  @observable newsletterSpecial = false
  @observable updatingNewsletterSubscription = 'unchanged' // unchanged / pending / done
  @observable newsletterErrors = []

  //Payment
  @observable bankOwner = ''
  @observable bankIBAN = ''
  @observable bankBic = ''
  @observable paypalOwner = ''
  @observable paypalEmail = ''
  @observable updatingPaymentData = 'unchanged' // unchanged / pending / done
  @observable paymentDataErrors = []


  @computed get passwordChangedSuccesfully() {
    return this.changingPassword === 'done'
  }

  @action.bound resetPasswordUpdatedSuccesfully() {
    this.changingPassword = 'unchanged'
  }

  @computed get emailChangedSuccesfully() {
    return this.changingEmail === 'done' && this.emailErrors.length === 0
  }

  @action.bound resetEmailUpdatedSuccesfully() {
    this.changingEmail = 'unchanged'
  }

  @computed get newsletterUpdatedSuccesfully() {
    return this.updatingNewsletterSubscription === 'done' && this.newsletterErrors.length === 0
  }

  @action.bound resetNewsletterUpdatedSuccesfully() {
    this.updatingNewsletterSubscription = 'unchanged'
  }

  @computed get personalDataUpdatedSuccesfully() {
    return this.updatingPersonalData === 'done' && this.personalDataErrors.length === 0
  }

  @action.bound resetDataUpdatedSuccesfully() {
    this.updatingPersonalData = 'unchanged'
  }

  @computed get paymentDataUpdatedSuccesfully() {
    return this.updatingPaymentData === 'done' && this.paymentDataErrors.length === 0
  }

  @action.bound resetPaymentUpdatedSuccesfully() {
    this.updatingPaymentData = 'unchanged'
  }

  @computed get deactivateAccountSuccess() {
    return this.deactivateAccountDone === 'done' && this.accountErrors.lenght === 0
  }

  constructor() {
    reaction(
      () => routingStore.location,
      () => {
        if (!commonStore.isLoggedIn && routingStore.location.pathname.startsWith('/profile')) {
          routingStore.push('/login')
        }
      },
      { name: '/profile needs Auth so redirect to /login ' }
    )

    reaction(
      () => routingStore.location,
      () => this.clearPasswords(),
      { name: 'clear passwords on location change' }
    )

    reaction(
      () => userStore.currentUser,
      () => this.populateStore(),
      { name: 'populate profileStore with userStore.currentUser data' }
    )

    reaction(
      () => routingStore.location,
      () => this.resetStore(),
      { name: 'reset the store' }
    )
  }

  @action.bound changePersonalData() {
    this.updatingPersonalData = 'pending'
    return api.User.updateCustomer({
      salutation: this.salutation,
      firstname: this.firstname,
      lastname: this.lastname,
      dateOfBirth: this.dateOfBirth,
      street: this.street,
      city: this.city,
      postcode: this.postcode,
      country: this.country,
      phoneFixed: this.phoneFixed,
      phoneMobile: this.phoneMobile,
    })
      .then(() => userStore.fetchUser())
      .catch(action('payment data change errored', err => { this.personalDataErrors.push(err) }))
      .finally(action('payment data change completed', () => { this.updatingPersonalData = 'done' }))
  }

  @action.bound changePaymentData() {
    this.updatingPaymentData = 'pending'
    return api.User.updateCashbackCustomer({
      bankBic: this.bankBic,
      bankIban: this.bankIBAN,
      bankOwner: this.bankOwner,
      paypalOwner: this.paypalOwner,
      paypalEmail: this.paypalEmail,
    })
      .then(() => userStore.fetchUser())
      .catch(action('payment data change errored', err => {
        this.paymentDataErrors.push(err)
        errorStore && errorStore.displayError(err.message)
      }))
      .finally(action('payment data change completed', () => { this.updatingPaymentData = 'done' }))
  }

  @action.bound setNewsletterBrand() {
    this.newsletterBrand = !this.newsletterBrand
  }

  @action.bound setNewsletterNews() {
    this.newsletterNews = !this.newsletterNews
  }

  @action.bound setNewsletterSpecial() {
    this.newsletterSpecial = !this.newsletterSpecial
  }

  @action.bound updateNewsletterSubscription() {
    this.updatingNewsletterSubscription = 'pending'
    return api.User.updateNewsletterSubscription({
      newsletterBrand: this.newsletterBrand,
      newsletterNews: this.newsletterNews,
      newsletterSpecial: this.newsletterSpecial
    })
      .then(() => userStore.fetchUser())
      .catch(action('newsletter change errored', err => { this.newsletterErrors.push(err) }))
      .finally(action('newsletter change completed', () => { this.updatingNewsletterSubscription = 'done' }))
  }

  @action.bound setPassword(password) {
    this.password = password
  }

  @action.bound setPasswordConfirm(password) {
    this.passwordConfirm = password
  }

  @action.bound changePassword() {
    if (!this.validatePasswords()) return Promise.resolve('validation failed')

    this.changingPassword = 'pending'
    return api.User.changePassword(this.password)
      .then(this.clearPasswords)
      .catch(err => this.passwordErrors.push(err))
      .finally(action('password change completed', () => { this.changingPassword = 'done' }))
  }

  @action.bound setEmail(email) {
    this.emailChanged = true
    this.email = email
  }

  @action.bound changeEmail() {
    if (!this.validateEmail()) return Promise.resolve('validation failed')

    this.changingEmail = 'pending'
    return api.User.changeEmail(this.email)
      .then(() => userStore.fetchUser())
      .catch(action('email change errored', err => this.emailErrors.push('email_already_in_use')))
      .finally(action('email change completed', () => { this.changingEmail = 'done' }))
  }

  @action.bound deactivateAccount() {
    this.deactivateAccountDone = 'pending'
    return api.User.deactivateAccount()
      .catch(action('deactivate account errored', err => this.accountErrors.push(err)))
      .finally(action('deactivate account completed', () => { this.deactivateAccountDone = 'done' }))
  }

  @action.bound terminateAccount() {
    return api.User.terminateAccount()
  }

  @action validatePasswords() {
    this.passwordErrors = []

    if (this.password.length < 6) this.passwordErrors.push('general_password_at_least_six')
    if (this.passwordConfirm !== this.password) this.passwordErrors.push('general_password_not_match')

    return !(this.passwordErrors.length > 0)
  }

  @action validateEmail() {
    this.emailErrors = []

    if (!(/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,5})+$/.test(this.email)))
      this.emailErrors.push('general_form_email_error')

    return !(this.emailErrors.length > 0)
  }

  @action.bound clearPasswords() {
    this.password = ''
    this.passwordConfirm = ''
    this.changingPassword = 'unchanged'
    this.passwordErrors = []
  }

  //PersonalData
  @action.bound setSalutation(salutation) {
    this.salutation = salutation
  }

  @action.bound setFirstname(firstname) {
    this.firstname = firstname
  }

  @action.bound setLastname(lastname) {
    this.lastname = lastname
  }

  @action.bound setDateOfBirth(dateOfBirth) {
    this.dateOfBirth = dateOfBirth
  }

  @action.bound setStreet(street) {
    this.street = street
  }

  @action.bound setCity(city) {
    this.city = city
  }

  @action.bound setPostcode(postcode) {
    this.postcode = postcode
  }

  @action.bound setCountry(country) {
    this.country = country
  }

  @action.bound setPhoneFixed(phoneFixed) {
    this.phoneFixed = phoneFixed
  }

  @action.bound setPhoneMobile(phoneMobile) {
    this.phoneMobile = phoneMobile
  }

  //Payment
  @action.bound setBankOwner(bankOwner) {
    this.bankOwner = bankOwner
  }

  @action.bound setBankIBAN(bankIBAN) {
    this.bankIBAN = bankIBAN
  }

  @action.bound setBankBic(bankBic) {
    this.bankBic = bankBic
  }

  @action.bound setPaypalOwner(paypalOwner) {
    this.paypalOwner = paypalOwner
  }

  @action.bound setPaypalEmail(paypalEmail) {
    this.paypalEmail = paypalEmail
  }

  @action populateStore() {
    if (userStore.currentUser) {
      this.customer = userStore.currentUser.customer
      this.newsletterBrand = userStore.currentUser.newsletterBrand || false
      this.newsletterNews = userStore.currentUser.newsletterNews || false
      this.newsletterSpecial = userStore.currentUser.newsletterSpecial || false
      this.email = userStore.currentUser.crm_customer.email || ''

      this.salutation = userStore.currentUser.crm_customer.salutation || ''
      this.firstname = userStore.currentUser.crm_customer.firstname || ''
      this.lastname = userStore.currentUser.crm_customer.lastname || ''
      this.dateOfBirth = userStore.currentUser.crm_customer.dateOfBirth || ''
      this.street = userStore.currentUser.crm_customer.street || ''
      this.city = userStore.currentUser.crm_customer.city || ''
      this.postcode = userStore.currentUser.crm_customer.postcode || ''
      this.country = userStore.currentUser.crm_customer.country || ''
      this.phoneFixed = userStore.currentUser.crm_customer.phoneFixed || ''
      this.phoneMobile = userStore.currentUser.crm_customer.phoneMobile || ''

      this.bankBic = userStore.currentUser.bankBic || ''
      this.bankIBAN = userStore.currentUser.bankIban || ''
      this.bankOwner = userStore.currentUser.bankOwner || ''
      this.paypalOwner = userStore.currentUser.paypalOwner || ''
      this.paypalEmail = userStore.currentUser.paypalEmail || ''
    }
  }

  @action.bound resetStore() {
    //its just looks like i copy pasted the variables and that is true :)
    this.password = ''
    this.passwordConfirm = ''
    this.changingPassword = 'unchanged' // unchanged / pending / done
    this.passwordErrors = []

    this.email = ''
    this.changingEmail = 'unchanged' // unchanged / pending / done
    this.emailErrors = []
    this.emailChanged = false

    this.deactivateAccountDone = 'unchanged' // unchanged / pending / done
    this.accountErrors = []

    //PersonalData
    this.salutation = ''
    this.firstname = ''
    this.lastname = ''
    this.dateOfBirth = ''
    this.street = ''
    this.city = ''
    this.postcode = ''
    this.country = ''
    this.phoneFixed = ''
    this.phoneMobile = ''
    this.updatingPersonalData = 'unchanged' // unchanged / pending / done
    this.personalDataErrors = []

    //Newsletter
    this.newsletterBrand = false
    this.newsletterNews = false
    this.newsletterSpecial = false
    this.updatingNewsletterSubscription = 'unchanged' // unchanged / pending / done
    this.newsletterErrors = []

    //Payment
    this.bankOwner = ''
    this.bankIBAN = ''
    this.bankBic = ''
    this.paypalOwner = ''
    this.paypalEmail = ''
    this.updatingPaymentData = 'unchanged' // unchanged / pending / done
    this.paymentDataErrors = []

    this.populateStore()
  }
}

export default new ProfileStore()



// WEBPACK FOOTER //
// ./src/stores/profileStore.js